import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { JobApplication } from '../types/job';
import { getJobApplications } from '../services/jobService';
import DashboardMetrics from '../components/features/analytics/DashboardMetrics';
import StatusDistribution from '../components/features/analytics/StatusDistribution';
import ApplicationTrends from '../components/features/analytics/ApplicationTrends';
import { motion } from 'framer-motion';
import { BarChart3, PieChart, TrendingUp } from 'lucide-react';

export function Analytics() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadJobs();
    }
  }, [user]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const jobData = await getJobApplications();
      setJobs(jobData);
    } catch (err: any) {
      setError(err.message || 'Failed to load job applications');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
            <div className="p-1.5 sm:p-2 bg-red-500/20 rounded-lg">
              <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-red-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Analytics Dashboard</h1>
          </div>
          <p className="text-gray-400 text-sm sm:text-lg">
            Comprehensive insights into your job application performance
          </p>
        </motion.div>


        {/* Main Analytics Grid */}
        <div className="space-y-6 sm:space-y-8">
          {/* Dashboard Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <DashboardMetrics applications={jobs} />
          </motion.div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Status Distribution */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                  <div className="p-1.5 sm:p-2 bg-red-500/20 rounded-lg">
                    <PieChart className="w-5 h-5 sm:w-6 sm:h-6 text-red-400" />
                  </div>
                  <h2 className="text-lg sm:text-xl font-semibold text-white">Status Distribution</h2>
                </div>
                <StatusDistribution data={jobs} />
              </div>
            </motion.div>

            {/* Application Trends */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                  <div className="p-1.5 sm:p-2 bg-blue-500/20 rounded-lg">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
                  </div>
                  <h2 className="text-lg sm:text-xl font-semibold text-white">Application Trends</h2>
                </div>
                <ApplicationTrends data={jobs} />
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </div>
  );
}
